import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/jyhdvnlr_logo-png-cuadrado.png";

// Datos legales centralizados
const EMPRESA = {
  nombre: "Muebles de Siempre, S.L.",
  marca: "CORE COLCHÓN",
  domicilio: "Crtra. de Extremadura, 8 — 45513 Santa Cruz del Retamar (Toledo)",
  cif: "B-83146654",
  tienda: "Calle Dehesa de Mari Martín, 27 (Autovía N-V) — 28600 Navalcarnero (Madrid)",
  telefono: "910 88 99 62",
  whatsapp: "+34 630 66 67 34",
  email: "navalcarnero@munozmuebles.net",
  web: "corecolchon-madrid.es",
};

const REVISION = "20 de mayo de 2026";

function LegalLayout({ title, kicker, children, testid }) {
  useEffect(() => {
    document.title = `${title} · Core Colchón`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div data-testid={testid} className="min-h-screen bg-[#F9F8FA] text-[#2E2638]">
      <header className="bg-white border-b border-[#E3DDE8]">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-back-top">
            <img src={LOGO} alt="Core Colchón" className="h-9 w-9 object-contain"/>
            <div>
              <div className="font-display font-bold leading-none">CORE COLCHÓN</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">Hecho en España</div>
            </div>
          </Link>
          <Link to="/" data-testid="legal-back-link" className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
            <ArrowLeft size={14}/> Volver
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-10 py-12 sm:py-16">
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B1538] mb-3">{kicker}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-3">{title}</h1>
        <p className="text-sm text-[#5C5366] mb-10">Última revisión: {REVISION}</p>
        <article className="prose prose-sm sm:prose-base max-w-none [&_h2]:font-display [&_h2]:text-[#2E2638] [&_h2]:font-bold [&_h2]:text-xl [&_h2]:mt-10 [&_h2]:mb-3 [&_h3]:font-bold [&_h3]:text-[#2E2638] [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-[#5C5366] [&_p]:leading-relaxed [&_li]:text-[#5C5366] [&_strong]:text-[#2E2638] [&_a]:text-[#8B1538] [&_a]:underline">
          {children}
        </article>

        <div className="mt-14 pt-8 border-t border-[#E3DDE8] text-xs text-[#5C5366]">
          <p className="font-bold uppercase tracking-wider text-[10px] text-[#2E2638] mb-2">Pies de página legales</p>
          <p>{EMPRESA.nombre} · CIF {EMPRESA.cif} · {EMPRESA.domicilio} · Tel. {EMPRESA.telefono} · {EMPRESA.email}</p>
        </div>
      </main>
    </div>
  );
}

// ===================== AVISO LEGAL =====================
export function AvisoLegal() {
  return (
    <LegalLayout testid="aviso-legal-page" kicker="Información legal" title="Aviso legal">
      <h2>1. Datos identificativos del titular del sitio web</h2>
      <p>
        En cumplimiento del artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la
        Información y de Comercio Electrónico (LSSI-CE), se informa a los usuarios de los siguientes datos:
      </p>
      <ul>
        <li><strong>Titular:</strong> {EMPRESA.nombre}</li>
        <li><strong>Marca comercial:</strong> {EMPRESA.marca}</li>
        <li><strong>CIF:</strong> {EMPRESA.cif}</li>
        <li><strong>Domicilio social:</strong> {EMPRESA.domicilio}</li>
        <li><strong>Tienda física:</strong> {EMPRESA.tienda}</li>
        <li><strong>Teléfono:</strong> {EMPRESA.telefono}</li>
        <li><strong>Correo electrónico:</strong> <a href={`mailto:${EMPRESA.email}`}>{EMPRESA.email}</a></li>
        <li><strong>Sitio web:</strong> {EMPRESA.web}</li>
      </ul>

      <h2>2. Objeto y ámbito de aplicación</h2>
      <p>
        El presente aviso legal regula el uso del sitio web {EMPRESA.web} (en adelante, "el Sitio Web"),
        propiedad de {EMPRESA.nombre}. La navegación por el Sitio Web atribuye la condición de Usuario e implica
        la aceptación plena y sin reservas de todas las disposiciones incluidas en este Aviso Legal, así como de
        la Política de Privacidad y la Política de Cookies.
      </p>

      <h2>3. Condiciones de uso</h2>
      <p>
        El Usuario se compromete a hacer un uso adecuado de los contenidos y servicios ofrecidos a través del
        Sitio Web y a no emplearlos para incurrir en actividades ilícitas o contrarias a la buena fe y al
        ordenamiento legal. Queda prohibido difundir contenidos de carácter racista, xenófobo, pornográfico,
        de apología del terrorismo o atentatorio contra los derechos humanos.
      </p>

      <h2>4. Propiedad intelectual e industrial</h2>
      <p>
        Todos los contenidos del Sitio Web (textos, fotografías, gráficos, imágenes, iconos, tecnología,
        software, así como su diseño gráfico y códigos fuente) son propiedad de {EMPRESA.nombre} o de sus
        legítimos titulares, sin que pueda entenderse que el uso del Sitio Web atribuya al Usuario derecho
        alguno sobre los mismos. La marca "{EMPRESA.marca}" y sus elementos gráficos son marcas registradas.
      </p>
      <p>
        El Usuario podrá visualizar e imprimir los contenidos del Sitio Web exclusivamente para su uso personal
        y privado, quedando prohibida su reproducción, distribución, comunicación pública o transformación con
        fines comerciales sin autorización expresa y por escrito del titular.
      </p>

      <h2>5. Exclusión de garantías y responsabilidad</h2>
      <p>
        {EMPRESA.nombre} no se hace responsable, en ningún caso, de los daños y perjuicios de cualquier
        naturaleza que pudieran ocasionar, a título enunciativo: errores u omisiones en los contenidos, falta
        de disponibilidad del portal o la transmisión de virus o programas maliciosos en los contenidos, a
        pesar de haber adoptado todas las medidas tecnológicas necesarias para evitarlo.
      </p>

      <h2>6. Enlaces a terceros</h2>
      <p>
        En el Sitio Web pueden existir enlaces a páginas de terceros que {EMPRESA.nombre} no puede controlar.
        Por tanto, no asume responsabilidad por los contenidos que puedan aparecer en dichas páginas.
      </p>

      <h2>7. Modificaciones</h2>
      <p>
        {EMPRESA.nombre} se reserva el derecho de efectuar sin previo aviso las modificaciones que considere
        oportunas en su Sitio Web, pudiendo cambiar, suprimir o añadir tanto los contenidos y servicios que se
        presten a través de la misma, como la forma en la que estos aparezcan presentados o localizados.
      </p>

      <h2>8. Legislación aplicable y jurisdicción</h2>
      <p>
        La relación entre {EMPRESA.nombre} y el Usuario se regirá por la normativa española vigente. Para la
        resolución de cualquier controversia, las partes se someten expresamente a los Juzgados y Tribunales
        de Madrid capital, con renuncia expresa a cualquier otro fuero que pudiera corresponderles.
      </p>
    </LegalLayout>
  );
}

// ===================== POLÍTICA DE PRIVACIDAD =====================
export function Privacidad() {
  return (
    <LegalLayout testid="privacidad-page" kicker="Protección de datos" title="Política de privacidad">
      <p>
        La presente Política de Privacidad regula el tratamiento de los datos personales facilitados por los
        Usuarios a {EMPRESA.nombre} (en adelante, "el Responsable") a través del sitio web {EMPRESA.web},
        conforme al Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018, de 5 de diciembre, de Protección
        de Datos Personales y garantía de los derechos digitales (LOPDGDD).
      </p>

      <h2>1. Responsable del tratamiento</h2>
      <ul>
        <li><strong>Razón social:</strong> {EMPRESA.nombre}</li>
        <li><strong>CIF:</strong> {EMPRESA.cif}</li>
        <li><strong>Domicilio:</strong> {EMPRESA.domicilio}</li>
        <li><strong>Correo electrónico:</strong> <a href={`mailto:${EMPRESA.email}`}>{EMPRESA.email}</a></li>
        <li><strong>Teléfono:</strong> {EMPRESA.telefono}</li>
      </ul>

      <h2>2. Datos que recogemos</h2>
      <p>A través del formulario de solicitud del cheque descuento de 100 €, recogemos los siguientes datos:</p>
      <ul>
        <li>Nombre y apellidos</li>
        <li>Dirección de correo electrónico</li>
        <li>Teléfono</li>
        <li>Preferencia de medida del colchón (opcional)</li>
        <li>Consentimiento explícito para el tratamiento</li>
      </ul>

      <h2>3. Finalidades del tratamiento</h2>
      <ul>
        <li>Gestionar la solicitud del cheque descuento y su envío al Usuario.</li>
        <li>Contactar al Usuario por teléfono, WhatsApp o correo electrónico para coordinar la visita a la tienda.</li>
        <li>Enviar comunicaciones comerciales relacionadas con productos {EMPRESA.marca}, siempre que el Usuario haya prestado su consentimiento.</li>
        <li>Cumplir con las obligaciones legales aplicables.</li>
      </ul>

      <h2>4. Base jurídica</h2>
      <p>
        La base legal para el tratamiento de los datos es el <strong>consentimiento expreso del Usuario</strong>
        otorgado al marcar la casilla correspondiente en el formulario, así como la <strong>ejecución de medidas
        precontractuales</strong> a petición del propio interesado (entrega del cheque descuento y posterior
        gestión comercial).
      </p>

      <h2>5. Conservación de los datos</h2>
      <p>
        Los datos personales se conservarán durante el tiempo necesario para cumplir con la finalidad para la
        que fueron recogidos y, en cualquier caso, durante los plazos legalmente exigibles. Si el Usuario no
        formaliza la compra, los datos serán eliminados transcurridos <strong>24 meses</strong> desde la última
        interacción comercial.
      </p>

      <h2>6. Destinatarios y cesiones</h2>
      <p>Los datos podrán ser comunicados a:</p>
      <ul>
        <li>Empresas proveedoras de servicios tecnológicos (alojamiento web, envío de correos transaccionales) bajo contrato de encargo de tratamiento.</li>
        <li>Administraciones públicas y autoridades cuando exista una obligación legal.</li>
      </ul>
      <p>No se realizan transferencias internacionales de datos fuera del Espacio Económico Europeo (EEE) salvo a proveedores acogidos a cláusulas contractuales tipo aprobadas por la Comisión Europea.</p>

      <h2>7. Derechos del Usuario</h2>
      <p>
        El Usuario puede ejercer en cualquier momento los siguientes derechos enviando un correo a{" "}
        <a href={`mailto:${EMPRESA.email}`}>{EMPRESA.email}</a> con el asunto "RGPD" y copia de su DNI:
      </p>
      <ul>
        <li><strong>Acceso:</strong> conocer qué datos suyos tratamos.</li>
        <li><strong>Rectificación:</strong> corregir datos inexactos.</li>
        <li><strong>Supresión:</strong> solicitar el borrado de sus datos ("derecho al olvido").</li>
        <li><strong>Oposición:</strong> oponerse a determinados tratamientos.</li>
        <li><strong>Limitación:</strong> solicitar la limitación del tratamiento.</li>
        <li><strong>Portabilidad:</strong> recibir sus datos en un formato estructurado y de uso común.</li>
        <li><strong>Revocación del consentimiento:</strong> retirar el consentimiento en cualquier momento sin que ello afecte a la licitud del tratamiento previo.</li>
      </ul>
      <p>
        Asimismo, el Usuario tiene derecho a presentar una reclamación ante la Agencia Española de Protección
        de Datos (<a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a>) si
        considera que sus derechos no han sido atendidos.
      </p>

      <h2>8. Medidas de seguridad</h2>
      <p>
        El Responsable ha adoptado las medidas técnicas y organizativas necesarias para garantizar la seguridad
        de los datos personales y evitar su alteración, pérdida, tratamiento o acceso no autorizado, habida
        cuenta del estado de la tecnología, la naturaleza de los datos almacenados y los riesgos a los que
        están expuestos.
      </p>

      <h2>9. Menores de edad</h2>
      <p>
        El Sitio Web no está dirigido a menores de 14 años. {EMPRESA.nombre} no recoge ni trata
        deliberadamente datos personales de menores. Si detectamos que se han facilitado datos de un menor sin
        el consentimiento de su tutor legal, procederemos a su eliminación inmediata.
      </p>
    </LegalLayout>
  );
}

// ===================== POLÍTICA DE COOKIES =====================
export function Cookies() {
  return (
    <LegalLayout testid="cookies-page" kicker="Información sobre cookies" title="Política de cookies">
      <p>
        El presente sitio web {EMPRESA.web} (titularidad de {EMPRESA.nombre}) utiliza cookies y tecnologías
        similares para mejorar la experiencia de navegación, analizar el uso del Sitio Web y medir el
        rendimiento de nuestras campañas publicitarias.
      </p>

      <h2>1. ¿Qué es una cookie?</h2>
      <p>
        Una cookie es un pequeño fichero de texto que se descarga en el dispositivo del Usuario al acceder a
        determinadas páginas web. Las cookies permiten, entre otras cosas, recordar las preferencias del Usuario,
        elaborar estadísticas sobre la navegación y mostrar publicidad relevante.
      </p>

      <h2>2. Tipos de cookies que utilizamos</h2>

      <h3>2.1. Cookies técnicas (necesarias)</h3>
      <p>
        Son imprescindibles para el correcto funcionamiento del Sitio Web (mantenimiento de la sesión, balanceo
        de carga, seguridad). No requieren consentimiento.
      </p>

      <h3>2.2. Cookies de análisis y rendimiento</h3>
      <p>
        Permiten cuantificar el número de visitantes y analizar de forma estadística cómo utilizan el Sitio Web,
        con el fin de mejorar la oferta de productos y servicios. Para esta finalidad utilizamos:
      </p>
      <ul>
        <li><strong>Google Tag Manager</strong> (proveedor: Google Ireland Ltd.) — gestiona la carga de etiquetas analíticas y de marketing.</li>
        <li><strong>Google Analytics</strong> (proveedor: Google Ireland Ltd.) — analiza el comportamiento agregado de los usuarios.</li>
      </ul>

      <h3>2.3. Cookies de publicidad y marketing</h3>
      <p>
        Permiten medir la eficacia de nuestras campañas y mostrar publicidad personalizada en otras plataformas
        a usuarios que hayan visitado nuestro Sitio Web. Pueden incluir, en su caso:
      </p>
      <ul>
        <li><strong>Meta Pixel</strong> (Facebook / Instagram) — atribución y remarketing en Meta.</li>
        <li><strong>Google Ads</strong> — atribución y remarketing en buscadores y red de display.</li>
      </ul>

      <h2>3. Gestión y revocación del consentimiento</h2>
      <p>
        Al acceder por primera vez al Sitio Web, el Usuario puede aceptar, rechazar o configurar el uso de
        cookies no esenciales. El consentimiento puede revocarse en cualquier momento eliminando las cookies
        almacenadas en el navegador mediante las opciones de configuración del propio navegador:
      </p>
      <ul>
        <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
        <li><a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
        <li><a href="https://support.apple.com/es-es/HT201265" target="_blank" rel="noopener noreferrer">Safari</a></li>
        <li><a href="https://support.microsoft.com/es-es/microsoft-edge" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
      </ul>

      <h2>4. Cookies de terceros</h2>
      <p>
        Algunas cookies son instaladas por terceros (Google, Meta) cuya política de privacidad puede
        consultarse en sus respectivos sitios web. {EMPRESA.nombre} no tiene control sobre las cookies de
        terceros más allá de habilitar o deshabilitar su carga desde el Sitio Web.
      </p>

      <h2>5. Plazo de conservación</h2>
      <p>
        Las cookies de sesión expiran al cerrar el navegador. Las cookies persistentes utilizadas para análisis
        y marketing tienen una duración máxima de <strong>24 meses</strong>, según las recomendaciones de la
        Agencia Española de Protección de Datos.
      </p>

      <h2>6. Actualizaciones</h2>
      <p>
        {EMPRESA.nombre} se reserva el derecho de actualizar esta Política de Cookies para adaptarla a nuevas
        exigencias legales o a cambios en la operativa del Sitio Web. Se recomienda al Usuario revisar
        periódicamente esta política.
      </p>
    </LegalLayout>
  );
}
